<template>
    <div class="inline-flex">
        <div class="btn btn-sm btn-blue" @click="settingsModal = true">
            Settings
            <i class="fas fa-caret-down ml-1"></i>
        </div>

        <modal @close="closeModal" ref="modalRef" v-if="settingsModal" id="modalSettings">
            <template #header>
                Settings
            </template>
            <template #body>
                <tabs class="tabs-modal" :options="{ useUrlFragment: false, defaultTabHash: defaultTab }">
                    <tab name="Display" tabid="display">
                        <div class="settings-wrapper">
                            <display-settings v-model="form" :form="form"></display-settings>
                        </div>
                    </tab>
                    <tab name="Scale" tabid="scale">
                        <div class="settings-wrapper">
                            <scale-settings v-model="form" :form="form"></scale-settings>
                        </div>
                    </tab>
                </tabs>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="closeModal">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" v-if="settingsModal !== 'tools'" @click="draw">Draw</div>
                </div>
            </template>
        </modal>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useSettingsFormatter } from '../Chart/mixins/SettingsFormatter';

import DisplaySettings from './DisplaySettings';
import ScaleSettings from './ScaleSettings';

const props = defineProps({
    initialSettings: Object
});

const $clone = (obj) => JSON.parse(JSON.stringify(obj));
const emit = defineEmits(['apply-settings']);

// Initial is the very first page load
const form = ref(Object.assign({}, props.initialSettings));

const settingsFormatter = useSettingsFormatter(form);

// Settings that have possibly changed
// User could then modify the settings, and reload the component
// Original is now what this state is
const originalSettings = ref($clone(props.initialSettings));
const settingsModal = ref(false);
const defaultTab = ref('display');

watch(() => props.initialSettings, (newSettings, oldSettings) => {
    form.value = Object.assign({}, newSettings);
    originalSettings.value = Object.assign({}, newSettings);
}, { deep: true });

const openDisplaySettings = () => {
    settingsModal.value = true;
    defaultTab.value = 'display';
};

const openScaleSettings = () => {
    settingsModal.value = true;
    defaultTab.value = 'scale';
};

const closeModal = () => {
    settingsModal.value = false;
    defaultTab.value = 'display';

    form.value = $clone(originalSettings.value);
};

const getActions = () => {
    if (form.value.density === 'custom') {
        form.value.density = form.value.customDensity;
    }

    // HACK for Vertical Line
    if (form.value.vertical && form.value.crosshair === false) {
        form.value.crosshair = 'vertical';

        setTimeout(() => {
            form.value.crosshair = false;
        }, 25);
    }

    if (form.value.crosshair === true) {
        form.value.crosshair = 'both';

        setTimeout(() => {
            form.value.crosshair = true;
        }, 25);
    }

    return Object.keys(form.value)
        .filter((key) => {
            // Comparisons are a special case, not a part of the true settings
            if (key === 'comparisons' || key === 'customDensity') return;

            return (originalSettings.value[key] !== form.value[key]) && form.value[key] !== undefined;
        }).map((key) => {
            // Located in SettingsFormatter.vue
            return settingsFormatter.format(key, form.value[key]);
        }).filter((key) => key !== undefined);
};

const draw = () => {
    emit('apply-settings', getActions());
    originalSettings.value = JSON.parse(JSON.stringify(form.value));
    settingsModal.value = false;
};

defineExpose({
    openDisplaySettings,
    openScaleSettings,
    form,
});
</script>

<style lang="scss">
#modalSettings {
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }
    }

    .settings-wrapper {
        @apply m-0 p-0 text-sm;
        .column-title {
            @apply font-bold mt-2 mb-1;
        }

        ul {
            label {
                @apply text-sm font-normal;
            }
        }

        .visual-controls {
            li {
                @apply inline-flex self-center mr-2;

                label {
                    @apply ml-1;
                }
            }
        }
    }
}
</style>
